import { useState } from "react"
import { useAppSession } from "../../Auth/useCubeApiToken"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { BinaryOperator, Query } from "@cubejs-client/core"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import dayjs from "dayjs"
import { DATE_FORMAT } from "~/ui-rtk/hooks/period"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"
import { type TColumnFiltersState } from "~/ui-rtk/constants/table-visualization-types"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"

export type TCreativeReport =
  | "ads_creative_id"
  | "ads_instagram_media_id"
  | "ads_instagram_permalink_url"
  | "ads_instagram_story_id"
  | "ads_object_story_id"
  | "ads_root_ad_name"
  | "ads_thumbnail_url"
  | "ads_video_id"

export const CREATIVE_REPORT: Record<string, TCreativeReport> = {
  ADS_CREATIVE_ID: "ads_creative_id",
  ADS_INSTAGRAM_MEDIA_ID: "ads_instagram_media_id",
  ADS_INSTAGRAM_PERMALINK_URL: "ads_instagram_permalink_url",
  ADS_INSTAGRAM_STORY_ID: "ads_instagram_story_id",
  ADS_OBJECT_STORY_ID: "ads_object_story_id",
  ADS_ROOT_AD_NAME: "ads_root_ad_name",
  ADS_THUMBNAIL_URL: "ads_thumbnail_url",
  ADS_VIDEO_ID: "ads_video_id",
}

export const CREATIVE_REPORT_OPTIONS: {
  label: string
  value: TCreativeReport
  primaryId: string
  skipPrimaryId?: boolean
}[] = [
  {
    label: "Ads Root Ad Name",
    value: CREATIVE_REPORT.ADS_ROOT_AD_NAME,
    primaryId: "root_ad_name",
  },
  {
    label: "Ads Creative Id",
    value: CREATIVE_REPORT.ADS_CREATIVE_ID,
    primaryId: "creative_id",
  },
  {
    label: "Ads Instagram Media Id",
    value: CREATIVE_REPORT.ADS_INSTAGRAM_MEDIA_ID,
    primaryId: "instagram_media_id",
  },
  {
    label: "Ads Instagram Permalink Url",
    value: CREATIVE_REPORT.ADS_INSTAGRAM_PERMALINK_URL,
    primaryId: "instagram_permalink_url",
  },
  {
    label: "Ads Instagram Story Id",
    value: CREATIVE_REPORT.ADS_INSTAGRAM_STORY_ID,
    primaryId: "instagram_story_id",
  },
  {
    label: "Ads Object Story Id",
    value: CREATIVE_REPORT.ADS_OBJECT_STORY_ID,
    primaryId: "object_story_id",
  },
  {
    label: "Ads Thumbnail Url",
    value: CREATIVE_REPORT.ADS_THUMBNAIL_URL,
    primaryId: "thumbnail_url",
    skipPrimaryId: true,
  },
  {
    label: "Ads Video Id",
    value: CREATIVE_REPORT.ADS_VIDEO_ID,
    primaryId: "video_id",
  },
]

export const CREATIVE_REPORTS_METRICS_MAP = {
  brand_value_agg: "Brand Value",
  d30_brand_value_agg: "Brand Value 30d",
  d60_brand_value_agg: "Brand Value 60d",
  d90_brand_value_agg: "Brand Value 90d",
  d120_brand_value_agg: "Brand Value 120d",
  d150_brand_value_agg: "Brand Value 150d",
  brand_value_over_spend: "Brand Value ROAS",
  d30_brand_value_over_spend: "Brand Value 30d ROAS",
  d60_brand_value_over_spend: "Brand Value 60d ROAS",
  d90_brand_value_over_spend: "Brand Value 90d ROAS",
  d120_brand_value_over_spend: "Brand Value 120d ROAS",
  d150_brand_value_over_spend: "Brand Value 150d ROAS",
  facebook_purchases: "Facebook Purchases",
  facebook_purchase_value: "Facebook Purchase Value",
  facebook_spend: "Facebook Spend",
  instagram_purchases: "Instagram Purchases",
  instagram_purchase_value: "Instagram Purchase Value",
  instagram_spend: "Instagram Spend",
  total_purchases: "Total Purchases",
  total_purchase_value: "Total Purchase Value",
  total_spend: "Total Spend",
  total_purchase_value_over_spend: "Total Purchase Value Over Spend",
  total_value: "Total Value",
  total_value_over_spend: "Total ROAS",
  ga4_total_revenue: "Ga4 Total Revenue",
  ga4_total_revenue_over_spend: "Ga4 Total Revenue Over Spend",
  facebook_comments: "Facebook Comments",
  facebook_likes: "Facebook Likes",
  facebook_shares: "Facebook Shares",
  facebook_follows: "Facebook Follows",
  facebook_saves: "Facebook Saves",
  instagram_comments: "Instagram Comments",
  instagram_likes: "Instagram Likes",
  instagram_shares: "Instagram Shares",
  instagram_follows: "Instagram Follows",
  instagram_business_profile_views: "Instagram Business Profile Views",
  instagram_saves: "Instagram Saves",
  total_comments: "Total Comments",
  total_likes: "Total Likes",
  total_shares: "Total Shares",
  total_follows: "Total Follows",
  total_saves: "Total Saves",
  serving_adsets: "Adsets where ad is live",
  active_adsets: "Total Live Adsets",
  percent_of_live_adsets: "Adset coverage",
}

// Categorize metrics by group
export const BRAND_METRICS: TMetricsMapKey[] = [
  "brand_value_agg",
  "d30_brand_value_agg",
  "d60_brand_value_agg",
  "d90_brand_value_agg",
  "d120_brand_value_agg",
  "d150_brand_value_agg",
  "brand_value_over_spend",
  "d30_brand_value_over_spend",
  "d60_brand_value_over_spend",
  "d90_brand_value_over_spend",
  "d120_brand_value_over_spend",
  "d150_brand_value_over_spend",
]

export const REVENUE_METRICS: TMetricsMapKey[] = [
  "facebook_purchase_value",
  "facebook_spend",
  "facebook_purchases",
  "instagram_purchase_value",
  "instagram_spend",
  "instagram_purchases",
  "total_purchase_value",
  "total_spend",
  "total_value",
  "total_value_over_spend",
  "total_purchases",
  "total_purchase_value_over_spend",
  "ga4_total_revenue",
  "ga4_total_revenue_over_spend",
]

export const ENGAGEMENT_METRICS: TMetricsMapKey[] = [
  "facebook_comments",
  "facebook_likes",
  "facebook_shares",
  "facebook_follows",
  "facebook_saves",
  "instagram_comments",
  "instagram_likes",
  "instagram_shares",
  "instagram_follows",
  "instagram_business_profile_views",
  "instagram_saves",
  "total_comments",
  "total_likes",
  "total_shares",
  "total_follows",
  "total_saves",
]

export const PERFORMANCE_METRICS: TMetricsMapKey[] = [
  "serving_adsets",
  "active_adsets",
  "percent_of_live_adsets",
]

export const ALL_METRICS = Object.keys(CREATIVE_REPORTS_METRICS_MAP)

export type TMetricsMapKey = keyof typeof CREATIVE_REPORTS_METRICS_MAP

export const ALWAYS_METRICS: TMetricsMapKey[] = [
  "total_spend",
  "brand_value_agg",
]

export const DEFAULT_METRICS: TMetricsMapKey[] = [
  "brand_value_over_spend",
  "total_value",
  "total_value_over_spend",
  "serving_adsets",
  "percent_of_live_adsets",
]

export const COEF_METRICS: TMetricsMapKey[] = [
  "brand_value_over_spend",
  "d30_brand_value_over_spend",
  "d60_brand_value_over_spend",
  "d90_brand_value_over_spend",
  "d120_brand_value_over_spend",
  "d150_brand_value_over_spend",
  "total_purchase_value_over_spend",
  "total_value_over_spend",
  "ga4_total_revenue_over_spend",
]

export const PERCENTAGE_METRICS: TMetricsMapKey[] = ["percent_of_live_adsets"]

export const MONETARY_METRICS: TMetricsMapKey[] = [
  "brand_value_agg",
  "d30_brand_value_agg",
  "d60_brand_value_agg",
  "d90_brand_value_agg",
  "d120_brand_value_agg",
  "d150_brand_value_agg",
  "facebook_purchase_value",
  "facebook_spend",
  "instagram_purchase_value",
  "instagram_spend",
  "total_purchase_value",
  "total_spend",
  "total_value",
  "ga4_total_revenue",
]

export const ITEMS_PER_PAGE = 12
const results: Record<string, unknown> = {}

function getQWithTimeDimensionAndDepVarFilters(
  q: Query,
  prefix: string,
  dateRange: [Date, Date],
  {
    depVar,
    depVarScaler,
  }: {
    depVar: string
    depVarScaler: string[]
  },
) {
  const filters = [
    {
      dimension: `${prefix}.dep_var`,
      operator: "equals" as BinaryOperator,
      values: [depVar],
    },
  ]
  if (depVarScaler.length > 0) {
    filters.push({
      dimension: `${prefix}.dep_var_scaler`,
      operator: "equals" as BinaryOperator,
      values: depVarScaler,
    })
  }
  return polyfillCubeQueryWithTimeDimension(
    {
      ...q,
      timeDimensions: [
        {
          dimension: `${prefix}.md_date`,
        },
      ],
      filters,
    },
    {
      dateRange,
    },
  )
}

const getBaseQuery = (prefix: string, metrics: TMetricsMapKey[]) => ({
  measures: [
    // ...ALWAYS_METRICS.map(metric => `${prefix}.${metric}`),
    ...metrics.filter(metric => !!metric).map(metric => `${prefix}.${metric}`),
  ],
})

export default function useConnect() {
  const [reportingItems, setReportingItems] = useState<Array<
    Record<string, string>
  > | null>(null)
  const [reportingItemsSummary, setReportingItemsSummary] = useState<Record<
    string,
    string
  > | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)
  const { cubeApi } = useAppSession()
  const { depVar, depVarScaler, amazonEnabled, toggleAmazon } = useDepVar()
  const requestReportingItems = async (
    source: TCreativeReport,
    dateRange: [Date, Date],
    page = 0,
    columnFilters?: TColumnFiltersState,
    sorting?: TSortingState,
  ) => {
    const { primaryId, skipPrimaryId } =
      CREATIVE_REPORT_OPTIONS.find(({ value }) => value === source) ?? {}

    if (!primaryId) {
      throw new Error(`No Creative Report cube found: ${source}`)
    }

    const dateRangeWithStarts: [Date, Date] = [
      dayjs(dateRange[0]).startOf("day").toDate(),
      dayjs(dateRange[1]).startOf("day").toDate(),
    ]
    setIsLoading(true)

    const cacheKey = `${source}.${dayjs(dateRange[0]).format(DATE_FORMAT)}-${dayjs(dateRange[1]).format(DATE_FORMAT)}-${page}-${JSON.stringify(columnFilters)}.${JSON.stringify(sorting)}-${depVar}-${JSON.stringify(depVarScaler)}`

    if (!results[cacheKey]) {
      const prefix = `company_media_metrics_${source}`
      const cubeFilters = useCubeFiltersFromColumnFilters(prefix, columnFilters)
      const q = {
        dimensions: [
          `${prefix}.thumbnail_url`,
          `${prefix}.instagram_permalink_url`,
          `${prefix}.all_live_ad_names`,
          `${prefix}.media_channel`,
          `${prefix}.all_live_adset_names`,
          `${prefix}.md_image_url`,
        ],
        ...getBaseQuery(prefix, ALL_METRICS as TMetricsMapKey[]),
        offset: page * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        total: true,
      } as Query

      if (!skipPrimaryId) {
        q.dimensions?.push(`${prefix}.${primaryId}`)
      }

      if (sorting) {
        q.order = [[`${prefix}.${sorting.id}`, sorting.desc ? "desc" : "asc"]]
      }

      const query = getQWithTimeDimensionAndDepVarFilters(
        q,
        prefix,
        dateRangeWithStarts,
        {
          depVar,
          depVarScaler,
        },
      )

      if (cubeFilters) {
        query.filters = [...(query.filters ?? []), ...cubeFilters]
      }

      const res = await cubeApi.load(query)

      results[cacheKey] = {
        data: res.rawData().map(item => {
          const noPrefixItm: Record<string, string> = {}
          Object.keys(item).forEach(keyWithPrefix => {
            const keyNoPrefix = keyWithPrefix.replace(`${prefix}.`, "")
            noPrefixItm[keyNoPrefix] = item[keyWithPrefix]
          })
          return noPrefixItm
        }),
        totalPages: Math.ceil((res.totalRows() ?? 0) / ITEMS_PER_PAGE) || 1,
      }
    }

    const cached = results[cacheKey] as {
      data: Array<Record<string, string>>
      totalPages: number
    }
    setReportingItems(cached.data)
    setTotalPages(cached.totalPages)
    setIsLoading(false)
  }

  const requestReportingItemsSummary = async (
    source: TCreativeReport,
    dateRange: [Date, Date],
    columnFilters?: TColumnFiltersState,
  ) => {
    setIsSummaryLoading(true)

    const dateRangeWithStarts: [Date, Date] = [
      dayjs(dateRange[0]).startOf("day").toDate(),
      dayjs(dateRange[1]).startOf("day").toDate(),
    ]

    const cacheKey = `${source}.${dayjs(dateRange[0]).format(DATE_FORMAT)}-${dayjs(dateRange[1]).format(DATE_FORMAT)}-summary-${depVar}-${JSON.stringify(depVarScaler)}`

    if (!results[cacheKey]) {
      const prefix = `company_media_metrics_${source}`
      const cubeFilters = useCubeFiltersFromColumnFilters(prefix, columnFilters)
      const q = {
        ...getBaseQuery(prefix, ALL_METRICS as TMetricsMapKey[]),
      }

      const query = getQWithTimeDimensionAndDepVarFilters(
        q,
        prefix,
        dateRangeWithStarts,
        {
          depVar,
          depVarScaler,
        },
      )

      if (cubeFilters) {
        query.filters = [...(query.filters ?? []), ...cubeFilters]
      }

      const res = await cubeApi.load(query)

      results[cacheKey] = res.rawData().map(item => {
        const noPrefixItm: Record<string, string> = {}
        Object.keys(item).forEach(keyWithPrefix => {
          const keyNoPrefix = keyWithPrefix.replace(`${prefix}.`, "")
          noPrefixItm[keyNoPrefix] = item[keyWithPrefix]
        })
        return noPrefixItm
      })[0]
    }

    setReportingItemsSummary(results[cacheKey] as Record<string, string>)
    setIsSummaryLoading(false)
  }

  return {
    isLoading: isLoading || isSummaryLoading,
    reportingItems,
    reportingItemsSummary,
    requestReportingItems,
    requestReportingItemsSummary,
    totalPages,
    depVar,
    depVarScaler,
    amazonEnabled,
    toggleAmazon,
  }
}
